import { Dialog, DialogContent, DialogTitle } from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import ButtonDialogClose from 'Components/_common/Buttons/ButtonDialogClose'
import ContentBookmarkEntity from 'Components/_common/ContentPreview/ContentBookmarkEntity'
import { useToggleBookmark } from 'Queries/Common'
import {
  useContestQualificationAcceptMapMutation,
  useContestQualificationChecklistQuery,
  useContestQualificationDiscardMapMutation,
  useContestQualificationSingleMapQuery,
  useContestQualificationStartMutation,
} from 'Queries/Contests'
import { useEffect, useState } from 'react'
import { Else, If, Then, When } from 'react-if'
import { useParams } from 'react-router-dom'
import { navigateToEmptyHash } from 'Scenes/navigators'

import { getIsEvaluationRound, getIsQualificationRound } from '../utils'
import ContestQualificationMapCheckImages from './ContestQualificationMapCheckImages'
import ContestQualificationMapCheckList from './ContestQualificationMapCheckList'
import { ContestQualificationMapControls, StartMapQualificationButton } from './ContestQualificationMapControls'
import { SkeletonQualificationCheckMap } from './ContestQualificationSkeletons'

const ContestQualificationMapCheckModal = ({ contestId }) => {
  const { mapId } = useParams()
  const [checkList, setCheckList] = useState([])

  const { data: initialCheckList } = useContestQualificationChecklistQuery(contestId)
  const {
    data: mapInfoData = {},
    isLoading: isMapLoading,
    isFetched,
  } = useContestQualificationSingleMapQuery(contestId, mapId)
  const { mutate: startQualifyingMap, isLoading: isQualifyingLoading } = useContestQualificationStartMutation(
    contestId,
    mapId,
  )
  const discardQuery = useContestQualificationDiscardMapMutation(contestId, mapId)
  const acceptQuery = useContestQualificationAcceptMapMutation(contestId, mapId)

  const {
    title,
    map_images,
    user_id,
    contest_maps,
    qualifications,
    locked_by,
    locked_by_me,
    locked_time_left,
    relations,
    game_id,
    halowaypoint_id,
    halowaypoint_deleted,
    is_bookmarked,
    published_time,
  } = mapInfoData

  const isMapLocked = !!locked_by
  const srcList = (map_images?.slice(0, 4) || []).map((i) => i?.urls?.small)
  const lastUpdatedByUsername = relations?.users?.[qualifications?.updated_by]?.login
  const userData = relations?.users?.[user_id] || {}
  const username = userData?.login
  const contestState = contest_maps?.state
  const isMapDiscarded = contest_maps?.discarded
  const isMapUnchecked = getIsQualificationRound(contestState) && !isMapDiscarded
  const isMapAccepted = getIsEvaluationRound(contestState) && !isMapDiscarded
  const { mutate: toggleBookmark, isLoading: isBookmarkLoading } = useToggleBookmark()

  const mapChecklist = qualifications?.contest_qualification_checklist_ids
  useEffect(() => {
    isFetched && setCheckList(mapChecklist)
  }, [isFetched, mapChecklist])

  const handleCheckboxChange = (e, id) => {
    const ids = e.target.checked ? [...checkList, id] : checkList.filter((i) => i !== id)
    startQualifyingMap(
      { ids },
      {
        onSuccess: () => setCheckList(ids),
      },
    )
  }

  return (
    <Dialog
      open
      fullWidth
      maxWidth="md"
      onClose={navigateToEmptyHash}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography sx={(theme) => ({ ...theme.typography.h4 })}>Map Details</Typography>
        <ButtonDialogClose
          isRelative
          onClick={navigateToEmptyHash}
        />
      </DialogTitle>
      <DialogContent>
        <If condition={!isFetched}>
          <Then>
            <SkeletonQualificationCheckMap />
          </Then>
          <Else>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '32px 0px' }}>
              <Typography sx={(theme) => ({ ...theme.typography.h4 })}>
                {title}
                <Typography
                  variant="span"
                  color="text.tertiary"
                >
                  {' '}
                  by{' '}
                </Typography>
                {username}
              </Typography>

              <ContentBookmarkEntity
                entityId={mapId}
                gameId={game_id}
                halowaypointId={halowaypoint_id}
                halowaypointDeleted={halowaypoint_deleted}
                publishedTime={published_time}
                isBookmarked={is_bookmarked}
                toggleBookmark={toggleBookmark}
                isLoading={isBookmarkLoading}
              />
            </Box>

            <ContestQualificationMapCheckImages srcList={srcList} />

            <When condition={isMapLocked || !isMapUnchecked}>
              <ContestQualificationMapCheckList
                initialCheckList={initialCheckList}
                checkList={checkList}
                checkListDisabled={isQualifyingLoading || isMapAccepted || isMapDiscarded}
                onCheck={handleCheckboxChange}
              />
            </When>

            <Box sx={{ marginTop: '32px' }}>
              <If condition={!isMapLocked && isMapUnchecked}>
                <Then>
                  <StartMapQualificationButton
                    isLoading={isQualifyingLoading || isMapLoading}
                    onClick={() => startQualifyingMap()}
                  />
                </Then>
                <Else>
                  <ContestQualificationMapControls
                    resetMap={startQualifyingMap}
                    resetMapIsLoading={isQualifyingLoading}
                    discardQuery={discardQuery}
                    acceptQuery={acceptQuery}
                    acceptDisabled={checkList?.length !== initialCheckList?.length}
                    isMapLockedByMe={locked_by_me}
                    lockedTimeLeft={locked_time_left}
                    lastUpdatedByUsername={lastUpdatedByUsername}
                    isMapUnchecked={isMapUnchecked}
                    isMapDiscarded={isMapDiscarded}
                    isMapAccepted={isMapAccepted}
                  />
                </Else>
              </If>
            </Box>
          </Else>
        </If>
      </DialogContent>
    </Dialog>
  )
}

export default ContestQualificationMapCheckModal
